// 🔁 Reemplaza/ajusta imports
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Client } from '../../../models/client';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import {
  Add as AddIcon,
  AddAlert as AddAlertIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import { useSelection } from '../../../components/context/SelectionContext';
import {
  useTheme, useMediaQuery, Tooltip, Button, Box, Card, Grid,
  FormControl, MenuItem, InputLabel, Select, CardContent
} from '@mui/material';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import ClientChangeHistoryModal from '../../../components/common/ClientChangeHistoryModal';
import PermissionButton from '../../../components/common/PermissionButton';
import ClientQuoteModal from '../../../components/common/ClientQuoteModal';


import { SimpleColumn, RowId, SimpleRow } from '../../../components/table/interface';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { useDrawer } from '../../../components/context/DrawerContext';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const Clientes: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Administrar Clientes";
  const { drawerOpen } = useDrawer(); // ya lo usas en tu componente
  const [clients, setClients] = useState<Client[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClientForHistory, setSelectedClientForHistory] = useState<Client | null>(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedClientForQuote, setSelectedClientForQuote] = useState<Client | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const { office_id, setOffice_id, collect_id, setCollect_id } = useSelection();

  // ✅ selección como RowId (string | number)
  const [selectedIds, setSelectedIds] = useState<RowId[]>([]);
  const { canCreate, canRead, canUpdate, canDelete, canUpdateQuote, canManageBackupAlert } = useResourcePermissions('/administrar/clientes');

  const [refresh, setRefresh] = useState(false);
  const sortByName = <T extends { name?: string }>(items: T[]): T[] =>
    [...items].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'es'));

  const handleOpenModal = (client: Client) => {
    setSelectedClientForHistory(client);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClientForHistory(null);
  };
  const handleOpenQuoteModal = (client: Client) => {
    setSelectedClientForQuote(client);
    setQuoteModalOpen(true);
  };
  const handleCloseQuoteModal = () => {
    setQuoteModalOpen(false);
    setSelectedClientForQuote(null);
  };
  const handleRefresh = () => setRefresh(prev => !prev);

  // ✅ Borrado acepta RowId y convierte a number para el API
  const handleDeleteSelected = async (idToDelete?: RowId) => {
    if (!canDelete) return;

    const idsToDelete: RowId[] = idToDelete !== undefined ? [idToDelete] : selectedIds;

    if (idsToDelete.length === 0) {
      showAlert("Debe seleccionar al menos un cliente para eliminar.", "warning");
      return;
    }

    if (!window.confirm(`¿Está seguro de eliminar ${idsToDelete.length} cliente(s)? Esta acción no se puede deshacer.`)) {
      return;
    }

    // convierto a números (si vienen como string) y valido
    const numericIds = idsToDelete.map(id => (typeof id === 'string' ? Number(id) : id))
      .filter(n => Number.isFinite(n)) as number[];

    if (numericIds.length === 0) {
      showAlert("IDs inválidos para eliminar.", "error");
      return;
    }

    showLoading();
    try {
      await axios.post('/clients/delete', { ids: numericIds });
      showAlert("Cliente(s) eliminado(s) correctamente", "success");
      setSelectedIds(prev => prev.filter(id => !numericIds.includes(typeof id === 'string' ? Number(id) : id)));
      handleRefresh();
    } catch (error) {
      showAlert("Ocurrió un error al eliminar", "error");
      console.error(error);
    } finally {
      hideLoading();
    }
  };

  // Cargar oficinas
  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const officesResponse = axios.get('offices');
        const collectsOfficeResponse = office_id ? axios.get(`collectsoffice/${office_id}`) : null;

        const [officesData, collectsOfficeData] = await Promise.all([
          officesResponse,
          collectsOfficeResponse,
        ]);

        if (officesData) {
          const officesList = Array.isArray(officesData?.data?.data) ? officesData.data.data : [];
          setOffices(sortByName(officesList));
        }
        if (collectsOfficeData) {
          const collectsList = Array.isArray(collectsOfficeData?.data) ? collectsOfficeData.data : [];
          setCollectsOffice(sortByName(collectsList));
        }
        setClients([]);
        await new Promise((r) => setTimeout(r, 500));
      } catch (error) {
        //console.log('Error fetching data:', error);
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, [office_id, refresh]);

  // Cargar clientes por collect_id
  useEffect(() => {
    const ClientesCollect = async () => {
      if (!collect_id) {
        setClients([]);
        return;
      }
      showLoading();
      try {
        const { data } = await axios.get(`collectclient/${collect_id}`);
        setClients(data.data);
        await new Promise((r) => setTimeout(r, 500));
        showAlert('Se ha realizado la consulta exitosamente!', 'info');
      } catch (error) {
        showAlert('Error fetching data!', 'error');
      } finally {
        hideLoading();
      }
    };
    ClientesCollect();
  }, [collect_id, refresh]);

  const hasBackups = clients.some((client: Client) => {
    const backups = (client as any).backups ?? [];
    return backups.length > 0 && Boolean((client as any).backup_alert_active);
  });

  // 👉 Columnas para SimpleCustomTable con opciones individuales
  const simpleColumns: SimpleColumn[] = [
    {
      field: 'idnumber',
      headerName: 'Cédula',
      minWidth: 100,
      maxWidth: 100,
    },
    {
      field: 'fullname',
      headerName: 'Cliente',
      minWidth: 140,
      maxWidth: 180,
      width: 260,
      wrapText: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', minWidth: 0 }}>
          {!row.hasActiveCredits && (
            <Tooltip title="No Crédito activo">
              <WarningIcon color="warning" sx={{ flexShrink: 0 }} fontSize="small" />
            </Tooltip>
          )}
          <Box
            component="span"
            sx={{
              minWidth: 0,
              flex: 1,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {row.fullname}
          </Box>
        </Box>
      ),
    },
    { field: 'phone', headerName: 'Teléfono', minWidth: 120, maxWidth: 160 },
    { field: 'cellphone', headerName: 'Celular', minWidth: 120, maxWidth: 160 },
    { field: 'mainaddress', headerName: 'Principal', minWidth: 120, maxWidth: 160, flex: 1 },
    { field: 'job', headerName: 'Ocupación', minWidth: 140, maxWidth: 220 },
    { field: 'quote', headerName: 'Cupo', minWidth: 100, maxWidth: 130 },

    // 🔹 Opciones individuales (alerta de backups)
    ...(
      hasBackups
        ? [{
          field: 'opt_backup',
          headerName: '',
          ...iconActionColumnSizing(isMobile),
          renderCell: ({ row }: { row: SimpleRow }) =>
            row.backups?.length > 0 && row.backupAlertActive ? (
              <PermissionButton
                allowed={canRead}
                deniedTooltip="Sin permiso para ver cambios del cliente"
                tooltip="Ver cambios del cliente"
                variant="contained"
                color="primary"
                sx={simpleTableIconButtonSx}
                onClick={() => handleOpenModal(row.sourceClient as Client)}
              >
                <AddAlertIcon sx={{ color: '#ff9800' }} fontSize="small" />
              </PermissionButton>
            ) : null,
        }]
        : []
    ),

    // 🔹 Editar
    {
      field: 'opt_edit',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => (
        <PermissionButton
          allowed={canUpdate}
          deniedTooltip="Sin permiso para editar"
          tooltip="Editar"
          variant="contained"
          color="primary"
          sx={simpleTableIconButtonSx}
          component={NavLink}
          to={`/administrar/clientes/${row.id}/editar`}
        >
            <EditIcon fontSize="small" />
        </PermissionButton>
      ),
    },
    {
      field: 'opt_quote',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => (
        <PermissionButton
          allowed={canUpdateQuote}
          deniedTooltip="Sin permiso para modificar cupo"
          tooltip="Modificar cupo"
          variant="contained"
          color="info"
          sx={simpleTableIconButtonSx}
          onClick={() => handleOpenQuoteModal(row.sourceClient as Client)}
        >
          <PersonIcon fontSize="small" />
        </PermissionButton>
      ),
    },

    // 🔹 Eliminar
    {
      field: 'opt_delete',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => (
        <PermissionButton
          allowed={canDelete}
          deniedTooltip="Sin permiso para eliminar"
          tooltip="Eliminar"
          variant="contained"
          color="error"
          sx={simpleTableIconButtonSx}
          onClick={() => handleDeleteSelected(row.id)}
        >
            <DeleteIcon fontSize="small" />
        </PermissionButton>
      ),
    },
  ];

  // filas para tabla simple
  const rows = clients.map((client: Client) => ({
    id: client.id as RowId,
    idnumber: client.idnumber,
    fullname: client.fullname,
    phone: client.phone,
    cellphone: client.cellphone,
    mainaddress: client.mainaddress,
    secondaddress: client.secondaddress,
    job: client.job,
    quote: Number((client as any).quote ?? 0),
    backups: (client as any).backups ?? [],        // si existe en payload
    backupAlertActive: Boolean((client as any).backup_alert_active),
    hasActiveCredits: (client as any).hasActiveCredits ?? false,
    collect: (client as any).collect,
    sourceClient: client,
  }));

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Información Clientes</Title>
        <Box sx={{ ml: 'auto' }}>
          <PermissionButton
            allowed={canCreate}
            deniedTooltip="Sin permiso para crear"
            tooltip="Agregar"
            variant="contained"
            startIcon={isMobile ? null : <AddIcon />}
            component={NavLink}
            to="/administrar/clientes/add"
          >
            {isMobile ? <AddIcon /> : "Agregar"}
          </PermissionButton>
          <PermissionButton
            allowed={canDelete}
            deniedTooltip="Sin permiso para eliminar"
            tooltip="Eliminar"
            variant="contained"
            color="error"
            startIcon={isMobile ? null : <DeleteIcon />}
            onClick={() => handleDeleteSelected()}
            disabled={selectedIds.length === 0}
            sx={{ ml: isMobile ? 1 : 2 }}
          >
            {isMobile ? <DeleteIcon /> : "Eliminar"}
          </PermissionButton>
        </Box>
      </StickyListToolbar>

      <Card
        sx={{
          width: (theme) => stickyListRegionWidth(theme, drawerOpen),
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <CardContent>
             <Grid container spacing={2}>
              <Grid item xs={12} sm={6} xl={6}>
                <FormControl fullWidth>
                  <InputLabel id="form-layouts-separator-select-label">Seleccionar Oficina</InputLabel>
                  {offices && offices.length > 0 ? (
                    <Select
                      label="Seleccionar Oficina"
                      value={office_id}
                      size='small'
                      id="form-layouts-separator-select"
                      labelId="form-layouts-separator-select-label"
                      onChange={(event) => setOffice_id(event.target.value)}
                      required
                    >
                      {offices.map((office: Office) => (
                        <MenuItem key={office.id} value={office.id}>{office.name}</MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} xl={6}>
                <FormControl fullWidth>
                  <InputLabel id="collect">Seleccionar Cobro</InputLabel>
                  {collectsOffice && collectsOffice.length > 0 ? (
                    <Select
                      label="Seleccionar Cobro"
                      value={collect_id}
                      size='small'
                      id="collects_id"
                      labelId="collects_id"
                      onChange={(event) => setCollect_id(event.target.value)}
                      required
                    >
                      {collectsOffice.map((collect: Collect) => (
                        <MenuItem key={collect.id} value={collect.id}>{collect.name}</MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </form>
        <Box>
          {clients.length > 0 ? (
            <CardContent
              sx={{
                width: (theme) => stickyListRegionWidth(theme, drawerOpen),
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflowX: 'auto',
                pt: 0,
                pb: 4,
              }}
            >
              <SimpleCustomTable
                rows={rows}
                columns={simpleColumns}
                enableSearch
                searchPlaceholder="Buscar cliente..."
                checkboxSelection
                selectionModel={selectedIds}
                onRowSelectionModelChange={setSelectedIds}
                disableInternalScroll
                rowHeight={24}
                autoRowHeight
                showBorders
                showCount
                exportCsv
                exportFileName="clientes.csv"
                wrapCellContent
              // (Opcional) agrupar acciones bajo un header “Opciones”
                columnGroups={[{ header: 'Opciones', fields: ['opt_backup','opt_edit','opt_quote','opt_delete'] }]}
              />
            </CardContent>
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </Box>
        <ClientChangeHistoryModal
          open={isModalOpen}
          onClose={handleCloseModal}
          showAlert={showAlert}
          showLoading={showLoading}
          hideLoading={hideLoading}
          canManageBackupAlert={canManageBackupAlert}
          onBackupAlertChange={(clientId, active) => {
            setClients((current) =>
              current.map((client) =>
                client.id === clientId
                  ? { ...client, backup_alert_active: active }
                  : client
              )
            );
            setSelectedClientForHistory((current) =>
              current && current.id === clientId
                ? { ...current, backup_alert_active: active }
                : current
            );
          }}
          clientData={selectedClientForHistory ? {
            id: selectedClientForHistory.id,
            backup_alert_active: Boolean((selectedClientForHistory as any).backup_alert_active),
            backups: (selectedClientForHistory as any).backups || [],
          } : null}
        />
        <ClientQuoteModal
          open={quoteModalOpen}
          client={selectedClientForQuote}
          onClose={handleCloseQuoteModal}
          onSaved={(nextQuote) => {
            setClients((current) =>
              current.map((client) =>
                client.id === selectedClientForQuote?.id
                  ? { ...client, quote: nextQuote }
                  : client
              )
            );
          }}
          showAlert={showAlert}
        />
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />
    </HeaderResponsive>
  );
};

export default Clientes;
